import { InboxOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Input, Modal, Space, theme, Typography } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import React, { useEffect, useState } from 'react';

import { useGetUploadSasUrl } from '@/api';
import { showMessage } from '@/shared/utils';

const ALLOWED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'text/csv': '.csv',
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'video/mp4': '.mp4',
  'video/quicktime': '.mov',
  'video/x-msvideo': '.avi',
};

const ALLOWED_FILE_EXTENSIONS = Object.values(ALLOWED_FILE_TYPES);

interface UploadFileModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  currentPath: string;
}

export function UploadFileModal({
  isOpen,
  onCancel,
  onSuccess,
  currentPath,
}: UploadFileModalProps) {
  const [form] = Form.useForm();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [baseName, setBaseName] = useState('');
  const [extension, setExtension] = useState('');
  const uploadFileMutation = useGetUploadSasUrl();
  const queryClient = useQueryClient();
  const { token } = theme.useToken();
  const [fileList, setFileList] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setSelectedFile(null);
      setBaseName('');
      setExtension('');
      setFileList([]);
    }
  }, [isOpen, form]);

  const handleFileSelect = (file: File) => {
    const name = file.name;
    const lastDot = name.lastIndexOf('.');
    if (lastDot > 0) {
      setBaseName(name.substring(0, lastDot));
      setExtension(name.substring(lastDot));
      form.setFieldsValue({ name: name.substring(0, lastDot) });
    } else {
      setBaseName(name);
      setExtension('');
      form.setFieldsValue({ name });
    }
    setSelectedFile(file);
    setFileList([
      {
        uid: `${Date.now()}-${file.name}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      },
    ]);
  };

  const handleOk = async () => {
    if (!selectedFile) {
      showMessage('error', 'Bitte wählen Sie eine Datei zum Hochladen');
      return;
    }

    try {
      const values = await form.validateFields();
      const fileName = (values.name || baseName) + extension;
      const filePath = `${currentPath}/${fileName}`;

      const response = await uploadFileMutation.mutateAsync(filePath);
      const uploadResponse = await fetch(response.url, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'x-ms-meta-orderIndex': String(response.nextOrderIndex),
        },
      });

      if (uploadResponse.ok) {
        showMessage('success', 'Datei erfolgreich hochgeladen');
        await queryClient.invalidateQueries({
          queryKey: ['blob-storage', 'folder-children', currentPath],
        });
        setFileList([]);
        onSuccess();
        onCancel();
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        form.setFields([
          {
            name: 'name',
            errors: [error.response.data.message],
          },
        ]);
      } else {
        showMessage(
          'error',
          error instanceof Error ? error.message : 'Hochladen der Datei fehlgeschlagen'
        );
      }
    }
  };

  const validateFileType = (file: File) => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = ALLOWED_FILE_EXTENSIONS.includes(extension);

    if (!isValidType) {
      showMessage(
        'error',
        `Dateityp nicht erlaubt. Erlaubte Typen: ${ALLOWED_FILE_EXTENSIONS.join(', ')}`
      );
      return false;
    }
    return true;
  };

  return (
    <Modal title='Datei hochladen' open={isOpen} onOk={handleOk} onCancel={onCancel}>
      <Form form={form} layout='vertical'>
        {selectedFile && (
          <Form.Item
            name='name'
            rules={[
              { max: 255, message: 'Es sind maximal 255 Zeichen erlaubt' },
              {
                pattern: /^[^\\/:*?"<>|]+$/,
                message: 'Dateiname darf keine Sonderzeichen enthalten (\\ / : * ? " < > |)',
              },
            ]}
          >
            <Input
              addonAfter={extension}
              placeholder='Dateiname (optional)'
              onChange={(e) => setBaseName(e.target.value)}
            />
          </Form.Item>
        )}
        <Dragger
          maxCount={1}
          beforeUpload={(file) => {
            if (validateFileType(file)) {
              handleFileSelect(file);
            }
            return false;
          }}
          onRemove={() => {
            setSelectedFile(null);
            setBaseName('');
            setExtension('');
            setFileList([]);
          }}
          accept={Object.keys(ALLOWED_FILE_TYPES).join(',')}
          fileList={fileList}
        >
          <Space direction={'vertical'} align={'center'}>
            <InboxOutlined style={{ fontSize: 48, color: token.colorFillSecondary }} />
            <Typography.Text type='secondary'>
              Klicken oder ziehen Sie die Datei zum Hochladen in diesen Bereich
            </Typography.Text>
            <Typography.Text type='secondary' style={{ fontSize: 12 }}>
              Allowed file types: {ALLOWED_FILE_EXTENSIONS.join(', ')}
            </Typography.Text>
          </Space>
        </Dragger>
      </Form>
    </Modal>
  );
}
